import { mediaUrl } from '@bluheadless/ui-logic/src/assets'
import { useNavigator } from '@bluheadless/ui-logic/src/hooks/store-locator/useNavigator'
import { useStoreLocatorStores } from '@bluheadless/ui-logic/src/hooks/store-locator/useStoreLocatorStores'
import { useCountries } from '@bluheadless/ui-logic/src/hooks/useCountries'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { cx } from '@emotion/css'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/system'
import merge from 'lodash/merge'
import uniqBy from 'lodash/uniqBy'
import { array, bool, func, number, object, oneOfType, shape, string } from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import useSupercluster from 'use-supercluster'
import { GoogleMap } from '../../molecules/google'
import { StoreLocatorList } from '../../molecules/store-locator/list'
import { StoreLocatorMarker } from '../../molecules/store-locator/marker'
import { StoreLocatorSearch } from '../../molecules/store-locator/search'
import AboveTheFold from '../../particles/above-the-fold'
import { generateClusterPoint, getDistance, getMapBounds, isValidCoordinates } from '../../utils/store-locator'
import { ClusterImage, ClusterMarker, InnerWrapper, Root } from './store-locator-columns.styled'

/**
 * This is only to render a React.Component as child of GoogleMap
 * since there are some spread props that are not supported by the pure HTML.
 * @reference [GitHub Issue](https://github.com/google-map-react/google-map-react/issues/213#issuecomment-238493461)
 */
const MarkerCluster = ({ children }) => children

const StoreLocatorColumns = ({
	apiConfigs,
	availabilityInStore,
	className,
	coordinates: _coordinates,
	listProps,
	mapProps: _mapProps,
	minHeight,
	pickUpInStore,
	searchProps,
	storeId,
	stores: storesProp = undefined,
}) => {
	const theme = useTheme()
	const lgUp = useMediaQuery(theme.breakpoints.up('lg'))

	const { stores: _stores } = useStoreLocatorStores(apiConfigs)
	const {
		storelocator: { geolocation, map, marker, tags },
	} = useConfig()

	const { shippingCountries } = useCountries()

	// Get user location
	const { coords: userPosition, isError } = useNavigator(geolocation.enabled)

	const { enabled: availabilityInStoreEnabled, hideNotAvailables } = availabilityInStore

	const { enabled: pickUpInStoreEnabled, onStoreSelect, selectedStoreId } = pickUpInStore

	/**
	 * Check if coordinates are valid, otherwise set them as undefined and use user location.
	 * User location will be always valid, since it is acquired from navigator.
	 */
	const coordinates = isValidCoordinates(_coordinates) ? _coordinates : undefined

	// https://reactjs.org/docs/hooks-faq.html#should-i-use-one-or-many-state-variables
	// Current marker opened
	const [activeStore, setActiveStore] = useState(null)
	const [allStores, setAllStores] = useState([])
	// Filters (tags)
	const [filters, setFilters] = useState({
		active: [], // Current active filters
		all: [],
	})
	// Map bounds
	const [mapBounds, setMapBounds] = useState({
		bounds: [],
		zoom: map.defaultZoom,
	})
	// Google Maps loaded
	const [mapsLoaded, setMapsLoaded] = useState(false)
	// Store points for clustering
	const [points, setPoints] = useState([])
	// Stores filtered by active filters
	const [stores, setStores] = useState([])
	// Whether to show the list and the search or not - will be set to false only the first time
	const [loading, setLoading] = useState(true)
	// Search performed or not filtered by active filters
	const [searchPerformed, setSearchPerformed] = useState(false)

	/**
	 * Generate clusters for the stores in the map.
	 * @reference [Supercluster](https://github.com/leighhalliday/use-supercluster)
	 */
	const { clusters, supercluster } = useSupercluster({
		points: points,
		bounds: mapBounds.bounds,
		zoom: mapBounds.zoom,
		options: {
			radius: 75, // Cluster radius, in pixels.
			maxZoom: 15, // Maximum zoom level at which clusters are generated.
			// for the other options -> https://github.com/mapbox/supercluster#options
		},
	})

	const mapRef = useRef()

	// deep merge props to avoid overriding the default ones
	const mapProps = merge(map, _mapProps)

	/**
	 * @description When the Google Maps API is loaded, set the map ref.
	 * @param {Object} map - Reference to the Google Map.
	 * @returns {void}
	 */
	const onGoogleApiLoaded = ({ map }) => {
		/**
		 * Save current map position and zoom when re-rendering.
		 * This is to keep the map centered when the viewport changes from mobile to desktop or vice versa.
		 */
		const currentCenter = mapRef?.current?.getCenter() ?? (coordinates || userPosition)
		const currentZoom = mapRef?.current?.getZoom() ?? map.defaultZoom
		mapRef.current = map
		if (currentCenter) mapRef.current.setCenter(currentCenter)
		if (currentZoom) mapRef.current.setZoom(currentZoom)
		setMapsLoaded(true)
	}

	/**
	 * @description When the map is moved, update the bounds.
	 * @param {LatLngBounds} bounds - Map bounds
	 * @returns {void}
	 * @reference [LatLngBounds](https://developers.google.com/maps/documentation/javascript/reference/coordinates#LatLngBounds)
	 */
	const onMapMove = ({ bounds, zoom }) => {
		// this below works with `google-map-react` but not with `@googlemaps/react-wrapper`
		// setMapBounds({ ...mapBounds, bounds: [bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat], zoom })
		const ne = bounds.getNorthEast()
		const sw = bounds.getSouthWest()
		// useSupercluster accepts bounds in the form of [westLng, southLat, eastLng, northLat]
		setMapBounds({ ...mapBounds, bounds: [sw.lng(), sw.lat(), ne.lng(), ne.lat()], zoom })
		// close the active marker when moving the map
		if (activeStore) setActiveStore(null)
	}

	/**
	 * @description This function is called when the user clicks on a store in the list.
	 * It'll scroll to the store in the list and center the map on the store.
	 * @param {number | string} id - Marker ID / Store ID
	 * @returns {void}
	 */
	const onStoreClick = useCallback(
		(id) => {
			const store = allStores.find((store) => store.id === id)
			if (store) {
				mapRef?.current?.setZoom(16)
				mapRef?.current?.panTo({
					lat: store.latitude,
					lng: store.longitude,
				})
				// wait for the map to be centered before opening the marker
				setTimeout(() => {
					// do not open marker if on mobile.
					setActiveStore(lgUp ? id : null)
				}, 2000)
			}
			document.getElementById(`storeListItem_${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
			if (listProps.onClick) listProps.onClick(id)
		},
		[allStores, lgUp, listProps]
	)

	/**
	 * @description This function is called when the user uses the search input.
	 * It centers the map to the searched location and updates the list of stores.
	 * @param {*} event - event
	 * @param {Object} value - value of search input
	 * @param {String} reason - change event
	 * @param {String} valueLabel - value of search input as string
	 * @param {{lat: number, lng: number}} placeCoords - coordinates of place
	 * @param {LatLngBounds=} viewport - viewport of place
	 * @returns {void}
	 */
	const onSearchChange = (event, value, reason, valueLabel, placeCoords, viewport, ...args) => {
		if (value) {
			if (placeCoords) {
				mapRef?.current?.setCenter(placeCoords)
				mapRef?.current?.panTo(placeCoords)
			}
			mapRef?.current?.setZoom(7)
			setSearchPerformed(true)
		}
		if (searchProps?.onChange) {
			searchProps.onChange(event, value, reason, valueLabel, placeCoords, viewport, ...args, { mapRef })
		}
	}

	/**
	 * @description - This function is called when the user clicks on a cluster on the map.
	 * @param {number | string} id - Cluster ID
	 * @param {number} lat - Latitude of the cluster
	 * @param {number} lng - Longitude of the cluster
	 */
	const onClusterClick = useCallback(
		(id, lat, lng) => {
			const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(id), 20)
			mapRef.current.setZoom(expansionZoom)
			mapRef.current.panTo({
				lat: lat,
				lng: lng,
			})
		},
		[supercluster]
	)

	/**
	 * @description - This function is called when the user clicks on a marker on the map.
	 * It'll update the list of stores and open (or close) the store info window.
	 * @param {number | string} id - Marker ID / Store ID
	 * @returns {void}
	 */
	const onMarkerClick = useCallback((id) => {
		document.getElementById(`storeListItem_${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
		setActiveStore((activeStore) => (activeStore === id ? null : id))
	}, [])

	/**
	 * @description - This function is called when the user closes a marker on the map.
	 * It'll update the state to close the marker.
	 * @param {number | string} id - Marker ID / Store ID
	 * @returns {void}
	 */
	// eslint-disable-next-line no-unused-vars
	const onMarkerClose = useCallback((id) => {
		setActiveStore(null)
	}, [])

	/**
	 * @description - This function is called when the user clicks on a filter.
	 * If a filter is checked, the stores will be filtered by the filter (tag)
	 * and the filter will be added to the active filters. Otherwise, it'll be removed.
	 * @param {event} event - event
	 * @param {number | string} filterId - Filter ID
	 * @param {boolean} isChecked - Whether the filter is checked or not
	 * @returns {void}
	 */
	const onFilterChange = (event, filterId, isChecked) => {
		let filteredStores = [...allStores]
		let activeFilters = [...filters.active]
		const points = []
		if (isChecked) {
			activeFilters.push(filterId)
		} else {
			activeFilters = activeFilters.filter((filter) => filter !== filterId)
		}
		// filter stores by checking if, in the store tags, there's, at least, one of the active filters
		if (activeFilters.length > 0)
			filteredStores = filteredStores.filter((store) => store.tags.some((tag) => activeFilters.includes(tag.id)))

		filteredStores.map((store) => points.push(generateClusterPoint(store)))

		setFilters({
			...filters,
			active: activeFilters,
		})
		setPoints(points)
		// no need to update stores: they will be updated when clusters are updated
		// setStores(filteredStores)
	}

	/**
	 * @description - This function is called when the user clicks on the "Reset" filters button.
	 * @param {*} event - event
	 * @returns {void}
	 */
	// eslint-disable-next-line no-unused-vars
	const onFiltersReset = (event) => {
		const points = []
		allStores.map((store) => points.push(generateClusterPoint(store)))
		setFilters({
			...filters,
			active: [],
		})
		setPoints(points)
		setStores(allStores)
	}

	useEffect(() => {
		if (allStores?.length > 0 && clusters) {
			let stores = []
			let storesInList = []
			// current map center which has just returned 0 results
			const centerLatLng = [mapRef?.current?.getCenter()?.lat(), mapRef?.current?.getCenter()?.lng()]

			clusters.map((cluster) => {
				const { cluster_id: clusterId, cluster: isCluster } = cluster.properties
				if (isCluster) {
					const clusterLeaves = supercluster.getLeaves(clusterId, Infinity)
					stores = [...stores, ...clusterLeaves]
				} else {
					stores = [...stores, cluster]
				}
			})

			storesInList = stores?.map((cluster) => cluster.store) || []

			if (storesInList?.length > 0) {
				// Sort stores by distance, if user position is available and geolocation is enabled.
				if ((coordinates || userPosition) && geolocation.enabled && !searchPerformed)
					storesInList?.sort((a, b) => a?.distanceInMt - b?.distanceInMt)

				// If availabilityInStore enabled and hideNotAvailables isn't,
				// sort the stores by availability.
				if (availabilityInStoreEnabled && !hideNotAvailables) {
					storesInList?.sort((a, b) =>
						a?.availableInStore && b?.availableInStore
							? a?.distanceInMt - b?.distanceInMt
							: a?.availableInStore
							? -1
							: 1
					)
				}

				if (!searchPerformed) {
					storesInList?.sort((a, b) => a?.distanceInMt - b?.distanceInMt)
					setStores(storesInList)
				} else {
					const alternativeStoresInList = storesInList.sort(
						(a, b) =>
							getDistance({ lat: centerLatLng[0], lng: centerLatLng[1] }, { lat: a.latitude, lng: a.longitude }) -
							getDistance({ lat: centerLatLng[0], lng: centerLatLng[1] }, { lat: b.latitude, lng: b.longitude })
					)

					setStores(alternativeStoresInList)
				}
			} else if (searchPerformed) {
				const alternativeStoresInList = allStores.sort(
					(a, b) =>
						getDistance({ lat: centerLatLng[0], lng: centerLatLng[1] }, { lat: a.latitude, lng: a.longitude }) -
						getDistance({ lat: centerLatLng[0], lng: centerLatLng[1] }, { lat: b.latitude, lng: b.longitude })
				)

				setStores(alternativeStoresInList)
				mapRef?.current?.setZoom(1)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allStores, availabilityInStoreEnabled, clusters, hideNotAvailables])

	useEffect(() => {
		if (
			(storesProp?.length || _stores?.length) &&
			mapsLoaded &&
			(!geolocation.enabled || (geolocation.enabled && (coordinates || userPosition || (!userPosition && isError))))
		) {
			const storesTags = []
			const points = []
			let stores = [...(storesProp ?? _stores)]
			let currentStore = null

			// If availabilityInStore and hideNotAvailables are enabled, filter stores by availability.
			if (availabilityInStoreEnabled && hideNotAvailables) stores = stores.filter((store) => store.availableInStore)

			// If pickUpInStore enabled, filter stores by the available shipping countries.
			if (pickUpInStoreEnabled)
				stores = stores.filter(
					(store) =>
						store.pickupInStoreEnabled &&
						shippingCountries.some((shippingCountry) => shippingCountry.code === store.countryCode)
				)

			stores?.map((store) => {
				if ((coordinates || userPosition) && geolocation.distance && !searchPerformed) {
					/**
					 * If we have starting coordinates, or if the user has enabled geolocation,
					 * we add the distance to the store from that position.
					 * This is approximate because the user's position is not precise
					 * and the distance is calculated using the Haversine formula.
					 */
					store.distanceInMt = getDistance(coordinates ?? userPosition, { lat: store.latitude, lng: store.longitude })
				}
				points.push(generateClusterPoint(store))
				// Get all the filters from the stores tags.
				if (tags.enabled) store.tags?.map((tag) => storesTags.push(tag))
			})

			currentStore = storeId ? stores?.find((store) => store.id === storeId) : null

			if ((pickUpInStoreEnabled && selectedStoreId) || currentStore || coordinates || userPosition) {
				// If we have a selected store, we center the map on it.
				if (pickUpInStoreEnabled && selectedStoreId) {
					const store = stores.find((store) => store.id === selectedStoreId)
					mapRef?.current?.panTo(store ? { lat: store.latitude, lng: store.longitude } : coordinates ?? userPosition)
				} else {
					mapRef?.current?.panTo(
						currentStore ? { lat: currentStore.latitude, lng: currentStore.longitude } : coordinates ?? userPosition
					)
				}
			} else {
				/**
				 * If the user has not enabled geolocation,
				 * we center the map trying to contain all the stores.
				 */
				const bounds = getMapBounds(stores)
				mapRef?.current?.fitBounds(bounds)
			}

			setFilters({
				...filters,
				all: uniqBy(storesTags, 'id'),
			})
			setPoints(points)
			setAllStores(stores)
			setLoading(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		availabilityInStoreEnabled,
		coordinates,
		isError,
		mapsLoaded,
		hideNotAvailables,
		_stores,
		storesProp,
		storeId,
		userPosition,
		pickUpInStoreEnabled,
	])

	const storeLocatorMap = () => (
		<GoogleMap mapMinHeight={minHeight} {...mapProps} onGoogleApiLoaded={onGoogleApiLoaded} onChange={onMapMove}>
			{clusters?.length > 0 &&
				clusters?.map(
					({
						id: clusterId,
						geometry: {
							coordinates: [clusterLongitude, clusterLatitude],
						},
						properties: { cluster: isCluster, point_count: pointCount },
						// If it is a cluster, store is undefined, so we assign it to null to avoid errors.
						store: { id, title, latitude, longitude, ...props } = {},
					}) => {
						return isCluster ? (
							<MarkerCluster key={`cluster-${clusterId}`} lat={clusterLatitude} lng={clusterLongitude}>
								{marker.clusterImage ? (
									<ClusterImage
										className="cluster-image"
										onClick={() => onClusterClick(clusterId, clusterLatitude, clusterLongitude)}
									>
										{/* eslint-disable-next-line @next/next/no-img-element */}
										<img src={mediaUrl(`filoblu/storelocator/${marker.clusterImage}`)} alt="cluster" />
										<span className="cluster-count">{pointCount}</span>
									</ClusterImage>
								) : (
									<ClusterMarker
										className="cluster-marker"
										style={{
											width: `${10 + (pointCount / points.length) * 20}px`,
											height: `${10 + (pointCount / points.length) * 20}px`,
										}}
										onClick={() => onClusterClick(clusterId, clusterLatitude, clusterLongitude)}
									>
										<span className="cluster-count">{pointCount}</span>
									</ClusterMarker>
								)}
							</MarkerCluster>
						) : (
							<StoreLocatorMarker
								key={id}
								className={['store-locator-marker', activeStore === id ? 'opened' : null].join(' ')}
								store={{
									storeId: id,
									latitude,
									longitude,
									name: title,
									...props,
								}}
								lat={latitude}
								lng={longitude}
								onClick={onMarkerClick}
								onClose={onMarkerClose}
								open={activeStore === id}
								markerProps={marker.image ? { image: mediaUrl(`filoblu/storelocator/${marker.image}`) } : {}}
								pickUpInStoreEnabled={pickUpInStoreEnabled}
								selectedStoreId={selectedStoreId}
								onStoreSelect={onStoreSelect}
								currentStore={allStores.find((store) => store.id === id)}
							/>
						)
					}
				)}
		</GoogleMap>
	)

	const storeLocatorSearch = () => (
		<StoreLocatorSearch
			activeFilters={filters.active}
			filters={filters.all}
			onFilterChange={onFilterChange}
			onFiltersReset={onFiltersReset}
			stores={stores?.length}
			{...searchProps}
			onChange={onSearchChange}
		/>
	)

	const storeLocatorList = () => (
		<StoreLocatorList
			availabilityInStore={availabilityInStore}
			stores={stores}
			{...listProps}
			onStoreClick={onStoreClick}
			pickUpInStore={pickUpInStore}
			variant="columns"
		/>
	)

	return (
		<Root className={cx('StoreLocator-root', className)}>
			{lgUp ? (
				<>
					{storeLocatorMap()}
					{mapsLoaded && !loading && (
						<AboveTheFold style={minHeight ? { minHeight: minHeight } : {}}>
							<InnerWrapper>
								{storeLocatorSearch()}
								{storeLocatorList()}
							</InnerWrapper>
						</AboveTheFold>
					)}
				</>
			) : (
				<>
					{storeLocatorSearch()}
					{storeLocatorMap()}
					{mapsLoaded && !loading && <InnerWrapper minHeight={minHeight}>{storeLocatorList()}</InnerWrapper>}
				</>
			)}
		</Root>
	)
}

StoreLocatorColumns.defaultProps = {
	apiConfigs: undefined,
	availabilityInStore: {
		enabled: false,
		hideNotAvailables: false,
	},
	minHeight: 700,
	pickUpInStore: {
		enabled: false,
		onStoreSelect: null,
		selectedStoreId: null,
	},
}

StoreLocatorColumns.propTypes = {
	/**
	 * Configs passed to the hook `useStorelocatorStores` to fetch the stores.
	 */
	apiConfigs: object,
	/**
	 * If enabled `true`, will show product availability in store.
	 * If hideNoAvailables `true`, will hide unavailable stores.
	 */
	availabilityInStore: shape({
		enabled: bool,
		hideNotAvailables: bool,
	}),
	/**
	 * Defines className for root node.
	 */
	className: string,
	/**
	 * The coordinates to initialize the map, for example, the ones passed as a query parameter.
	 * They will override the user's position.
	 * @example {lat: 45.484825, lng: 12.0023784}
	 */
	coordinates: shape({
		lat: number.isRequired,
		lng: number.isRequired,
	}),
	/**
	 * Props passed to the StoreLocatorList component.
	 */
	listProps: shape({ ...StoreLocatorList.propTypes, stores: array }),
	/**
	 * Props passed to the GoogleMap component.
	 */
	mapProps: shape({ ...GoogleMap.propTypes, ...{ defaultCenter: object, defaultZoom: number } }),
	/**
	 * Minimum height of the map and the container of the Search and the List Components.
	 *
	 * @default 700
	 */
	minHeight: number,
	/**
	 * Enable Pick Up In Store.
	 */
	pickUpInStore: shape({
		enabled: bool,
		onStoreSelect: func,
		selectedStoreId: oneOfType([string, number]),
	}),
	/**
	 * Props passed to the StoreLocatorSearch component.
	 */
	searchProps: shape({ ...StoreLocatorSearch.propTypes }),
	/**
	 * Pre-select a store on the map.
	 */
	storeId: oneOfType([string, number]),
}

export default StoreLocatorColumns
